/**
 * Painel de filtros da fila (ADR-0069): visões salvas, origem, comprador,
 * datas, prioridade e toggles. As visões salvas ficam no localStorage do
 * tenant (preferência da pessoa, não dado operacional).
 */
import React, { useEffect, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";

import SlideOverPanel from "../../../components/SlideOverPanel";
import { ORIGENS_REQUISICAO, TIPOS_TERCEIRO } from "../../../services/compras";
import { getItemTenant, setItemTenant } from "../../../utils/tenantStorage";
import { Chip } from "../_shared/quadro/styles";
import { Avatar, Rotulo, Segmented, ToggleLinha } from "./ui";

const CHAVE_VISOES = "req-compra:visoes-salvas";

export const FILTROS_PADRAO = {
  origem: [], comprador: [], solicitante: [], criadaEm: "", prazoAte: "", prioridade: "",
  soAtrasadas: false, semAnexo: false, incluirCanceladas: false, situacao: "",
  // ADR-0078: "de terceiros" = tem ao menos um item de terceiro, venha de
  // onde vier; e o período exato (a semana que o comprador quer ver).
  soTerceiros: false, tipoTerceiro: [], criadaDe: "", criadaAte: "",
};

const VISOES_FIXAS = [
  { id: "minha-fila", nome: "Minha fila", filtros: { ...FILTROS_PADRAO }, fixa: true, meu: true },
  { id: "atrasadas", nome: "Atrasadas", filtros: { ...FILTROS_PADRAO, soAtrasadas: true }, fixa: true },
  { id: "terceiros", nome: "Terceiros", filtros: { ...FILTROS_PADRAO, soTerceiros: true }, fixa: true },
  { id: "terceiros-sem-anexo", nome: "Terceiros sem anexo", filtros: { ...FILTROS_PADRAO, soTerceiros: true, semAnexo: true }, fixa: true },
];

export function lerVisoesSalvas() {
  try {
    const salvas = JSON.parse(getItemTenant(CHAVE_VISOES) || "[]");
    return [...VISOES_FIXAS, ...(Array.isArray(salvas) ? salvas : [])];
  } catch {
    return VISOES_FIXAS;
  }
}

function gravarVisoes(visoes) {
  setItemTenant(CHAVE_VISOES, JSON.stringify(visoes.filter((v) => !v.fixa)));
}

/** Quantos filtros estão ativos (para o badge do botão). */
export function contarFiltrosAtivos(f) {
  let n = 0;
  if (f.origem?.length) n += 1;
  if (f.comprador?.length) n += 1;
  if (f.solicitante?.length) n += 1;
  if (f.criadaEm) n += 1;
  if (f.prazoAte) n += 1;
  if (f.prioridade) n += 1;
  if (f.soAtrasadas) n += 1;
  if (f.semAnexo) n += 1;
  if (f.situacao) n += 1;
  if (f.soTerceiros) n += 1;
  if (f.tipoTerceiro?.length) n += 1;
  if (f.criadaDe || f.criadaAte) n += 1;
  return n;
}

const CAMPO = {
  height: 36, padding: "0 10px", borderRadius: 8, border: "1px solid var(--border)",
  background: "var(--surface)", color: "var(--ink)", font: "inherit", fontSize: 13, width: "100%", boxSizing: "border-box",
};

export default function FiltrosPanel({
  open, onClose, filtros, onAplicar, pessoas = [], solicitantes = [], totalFiltrado, totalGeral, canceladas = 0, meuId,
}) {
  const [f, setF] = useState(filtros);
  const [visoes, setVisoes] = useState(() => lerVisoesSalvas());
  const [visaoAtiva, setVisaoAtiva] = useState(null);
  const [salvandoNome, setSalvandoNome] = useState(false);
  const [nome, setNome] = useState("");

  useEffect(() => { if (open) { setF(filtros); setVisaoAtiva(null); setSalvandoNome(false); } }, [open, filtros]);

  const set = (campo, valor) => setF((p) => ({ ...p, [campo]: valor }));
  const toggleLista = (campo, valor) => setF((p) => {
    const atual = p[campo] || [];
    return { ...p, [campo]: atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual, valor] };
  });

  function aplicarVisao(v) {
    setVisaoAtiva(v.id);
    const novo = { ...FILTROS_PADRAO, ...v.filtros };
    if (v.meu && meuId) novo.comprador = [meuId];
    setF(novo);
  }

  function salvarAtual() {
    const n = nome.trim();
    if (!n) return;
    const nova = { id: `v_${Date.now().toString(36)}`, nome: n, filtros: { ...f } };
    const lista = [...visoes, nova];
    setVisoes(lista);
    gravarVisoes(lista);
    setNome("");
    setSalvandoNome(false);
    setVisaoAtiva(nova.id);
  }

  function removerVisao(id) {
    const lista = visoes.filter((v) => v.id !== id);
    setVisoes(lista);
    gravarVisoes(lista);
  }

  const ativos = contarFiltrosAtivos(f);

  const footer = (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
      <button type="button" className="btn btn--secondary" onClick={() => setF({ ...FILTROS_PADRAO })}>Limpar tudo</button>
      <button type="button" className="btn btn--primary" onClick={() => onAplicar(f)}>Aplicar filtros</button>
    </div>
  );

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Filtros"
      subtitle={`${ativos} ativo${ativos === 1 ? "" : "s"} · ${totalFiltrado ?? 0} de ${totalGeral ?? 0} requisições`}
      width="min(94vw, 440px)"
      footer={footer}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <Rotulo>Visões salvas</Rotulo>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {visoes.map((v) => (
              <Chip key={v.id} type="button" $on={visaoAtiva === v.id} onClick={() => aplicarVisao(v)} style={{ borderRadius: 8, height: 30 }}>
                {v.nome}
                {!v.fixa ? (
                  <span className="x" role="button" aria-label={`Remover visão ${v.nome}`} onClick={(e) => { e.stopPropagation(); removerVisao(v.id); }}>
                    <FiX size={12} />
                  </span>
                ) : null}
              </Chip>
            ))}
            {salvandoNome ? (
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <input
                  autoFocus
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") salvarAtual(); if (e.key === "Escape") setSalvandoNome(false); }}
                  placeholder="Nome da visão"
                  style={{ ...CAMPO, height: 30, width: 160 }}
                />
                <button type="button" className="btn btn--primary btn--sm" onClick={salvarAtual} disabled={!nome.trim()}>Salvar</button>
              </span>
            ) : (
              <Chip type="button" onClick={() => setSalvandoNome(true)} style={{ borderRadius: 8, height: 30, borderStyle: "dashed" }}>
                <FiPlus size={12} /> Salvar atual
              </Chip>
            )}
          </div>
        </div>

        <div>
          <Rotulo>Origem</Rotulo>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {ORIGENS_REQUISICAO.map((o) => (
              <Chip key={o.value} type="button" $on={(f.origem || []).includes(o.value)} onClick={() => toggleLista("origem", o.value)}>
                {o.label}
              </Chip>
            ))}
          </div>
        </div>

        <div>
          <Rotulo>Terceiros</Rotulo>
          <ToggleLinha
            titulo="Só requisições com item de terceiro"
            descricao="Vale para qualquer origem: basta ter um item de terceiro."
            on={Boolean(f.soTerceiros)}
            onChange={(v) => set("soTerceiros", v)}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {TIPOS_TERCEIRO.map((t) => (
              <Chip key={t.value} type="button" $on={(f.tipoTerceiro || []).includes(t.value)} onClick={() => toggleLista("tipoTerceiro", t.value)}>
                {t.label}
              </Chip>
            ))}
          </div>
        </div>

        <div>
          <Rotulo>Comprador</Rotulo>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {pessoas.map((p) => (
              <Chip key={p.id} type="button" $on={(f.comprador || []).includes(p.id)} onClick={() => toggleLista("comprador", p.id)} style={{ paddingLeft: 4 }}>
                <Avatar nome={p.nome} size={20} /> {p.nome.split(" ").slice(0, 2).join(" ")}
              </Chip>
            ))}
            <Chip type="button" $on={(f.comprador || []).includes("nenhum")} onClick={() => toggleLista("comprador", "nenhum")}>
              Sem responsável
            </Chip>
          </div>
        </div>

        <div>
          <Rotulo>Solicitante</Rotulo>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {solicitantes.map((p) => (
              <Chip key={p.id} type="button" $on={(f.solicitante || []).includes(p.id)} onClick={() => toggleLista("solicitante", p.id)} style={{ paddingLeft: 4 }}>
                <Avatar nome={p.nome} size={20} /> {p.nome.split(" ").slice(0, 2).join(" ")}
              </Chip>
            ))}
            {!solicitantes.length ? <span style={{ fontSize: 12, color: "var(--ink-4)" }}>Nenhum solicitante na fila.</span> : null}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <Rotulo>Criada de</Rotulo>
            <input type="date" value={f.criadaDe || ""} max={f.criadaAte || undefined} onChange={(e) => set("criadaDe", e.target.value)} style={CAMPO} aria-label="Criada a partir de" />
          </div>
          <div>
            <Rotulo>Criada até</Rotulo>
            <input type="date" value={f.criadaAte || ""} min={f.criadaDe || undefined} onChange={(e) => set("criadaAte", e.target.value)} style={CAMPO} aria-label="Criada até" />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <Rotulo>Criada em</Rotulo>
            <select value={f.criadaEm || ""} onChange={(e) => set("criadaEm", e.target.value)} style={CAMPO}>
              <option value="">Qualquer data</option>
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
            </select>
          </div>
          <div>
            <Rotulo>Prazo até</Rotulo>
            <input type="date" value={f.prazoAte || ""} onChange={(e) => set("prazoAte", e.target.value)} style={CAMPO} />
          </div>
        </div>

        <div>
          <Rotulo>Prioridade</Rotulo>
          <Segmented
            ariaLabel="Prioridade"
            opcoes={[{ value: "", label: "Todas" }, { value: "URGENTE", label: "Urgente", tone: "danger" }, { value: "NORMAL", label: "Normal" }]}
            valor={f.prioridade || ""}
            onChange={(v) => set("prioridade", v)}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <ToggleLinha titulo="Só atrasadas" descricao="Prazo de cotação ou entrega vencido" on={!!f.soAtrasadas} onChange={(v) => set("soAtrasadas", v)} />
          <ToggleLinha titulo="Terceiros sem detalhamento" descricao="Itens que não dão para orçar" on={!!f.semAnexo} onChange={(v) => set("semAnexo", v)} />
          <ToggleLinha titulo="Incluir canceladas" descricao={`Ocultas por padrão (${canceladas})`} on={!!f.incluirCanceladas} onChange={(v) => set("incluirCanceladas", v)} />
        </div>
      </div>
    </SlideOverPanel>
  );
}
